import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
  Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Children, cloneElement, isValidElement } from 'react';
import { COLORS, paperSx, scrollSx } from './formTheme';

export default function MainDialog({
  children,
  title,
  steps,
  open,
  setOpen,
  validateStep,
  onSubmit,
  submitting = false,
  submitError = '',
  isDirty = false,
  initialStep = 0,
  onStepChange,
  restored = false,
  onStartOver,
  onDiscard,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { lang } = useParams();
  const { t } = useTranslation();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(Math.min(initialStep, steps.length - 1));
  const [stepError, setStepError] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const contentRef = useRef(null);
  const firstRender = useRef(true);

  const isLast = activeStep === steps.length - 1;

  // new step: start at the top and clear the previous step's warning
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
    setStepError(false);
    if (firstRender.current) firstRender.current = false;
    else onStepChange?.(activeStep);
  }, [activeStep]);

  const leave = (discard = false) => {
    if (discard) onDiscard?.();
    setConfirmClose(false);
    setOpen(false);
    navigate(`/${lang}`);
  };

  const handleClose = () => (isDirty ? setConfirmClose(true) : leave());

  const scrollToFirstError = () =>
    setTimeout(() => {
      contentRef.current
        ?.querySelector('.Mui-error, [aria-invalid="true"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);

  const handleNext = async () => {
    if (await validateStep(activeStep)) {
      setStepError(false);
      setActiveStep((prev) => prev + 1);
    } else {
      setStepError(true);
      scrollToFirstError();
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);
  const goToStep = (step) => setActiveStep(step);

  const primaryLabel = isLast
    ? submitting
      ? t('processing')
      : t('submit')
    : t('btn_next');

  return (
    <>
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason !== 'backdropClick') handleClose();
        }}
        fullScreen={fullScreen}
        fullWidth
        maxWidth={false}
        slotProps={{
          paper: {
            sx: paperSx({ fullScreen, fixedHeight: true }),
            elevation: 6,
          },
        }}
      >
        {/* sticky header */}
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant='h6' component='span' sx={{ fontWeight: 700 }}>
                {title}
              </Typography>
              <Typography
                sx={{ color: COLORS.muted, fontSize: '0.9rem', mt: 0.5 }}
                aria-live='polite'
              >
                {t('step_of', { current: activeStep + 1, total: steps.length })}
                {' · '}
                <Box component='span' sx={{ color: '#fff', fontWeight: 600 }}>
                  {steps[activeStep]}
                </Box>
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              aria-label={t('close_leave')}
              sx={{ color: 'white', mt: -0.5, mr: -1 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <LinearProgress
          variant='determinate'
          value={((activeStep + 1) / steps.length) * 100}
          sx={{
            height: 4,
            backgroundColor: 'rgba(255,255,255,0.12)',
            '& .MuiLinearProgress-bar': { backgroundColor: COLORS.accent },
          }}
        />

        {/* only this area scrolls */}
        <DialogContent
          ref={contentRef}
          component='form'
          noValidate
          onSubmit={(e) => e.preventDefault()}
          sx={{
            flex: 1,
            p: fullScreen ? 2 : 4,
            pt: fullScreen ? 3 : 4,
            ...scrollSx,
          }}
        >
          {restored && activeStep === 0 && (
            <Alert
              severity='info'
              variant='outlined'
              sx={{ mb: 3, color: COLORS.text, borderColor: COLORS.borderStrong }}
              action={
                <Button
                  color='inherit'
                  size='small'
                  onClick={() => {
                    onStartOver?.();
                    setActiveStep(0);
                  }}
                >
                  {t('start_over')}
                </Button>
              }
            >
              {t('draft_restored')}
            </Alert>
          )}
          <Fade in key={activeStep}>
            <Box>
              {Children.toArray(children).map((child, i) =>
                i === activeStep && isValidElement(child) && typeof child.type !== 'string'
                  ? cloneElement(child, { goToStep })
                  : i === activeStep
                    ? child
                    : null,
              )}
            </Box>
          </Fade>
        </DialogContent>

        {/* sticky footer */}
        <Box sx={{ borderTop: `1px solid ${COLORS.border}` }}>
          {(stepError || submitError) && (
            <Alert
              severity='error'
              variant='outlined'
              role='alert'
              sx={{ mx: 2, mt: 2, color: '#ff8a8a', borderColor: '#ff5f5f' }}
            >
              {submitError || t('fix_errors')}
            </Alert>
          )}
          <DialogActions
            sx={{ p: 2, justifyContent: 'space-between', gap: 1 }}
          >
            <Button
              disabled={activeStep === 0 || submitting}
              onClick={handleBack}
              type='button'
              sx={{
                fontWeight: 600,
                minHeight: 44,
                color: 'white',
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
              }}
            >
              {t('btn_back')}
            </Button>
            <Button
              variant='contained'
              type='button'
              disabled={submitting}
              onClick={isLast ? onSubmit : handleNext}
              sx={{
                fontWeight: 700,
                minHeight: 44,
                minWidth: 140,
                backgroundColor: COLORS.accent,
                color: COLORS.onAccent,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.85)' },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(255,255,255,0.5)',
                  color: 'rgba(18,18,18,0.6)',
                },
              }}
            >
              {primaryLabel}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>{t('close_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('close_body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)} autoFocus>
            {t('close_stay')}
          </Button>
          <Button onClick={() => leave(true)} color='error'>
            {t('close_discard')}
          </Button>
          <Button onClick={() => leave(false)}>{t('close_leave')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
